// src/components/exercise/ProgressRing.tsx
// Reusable SVG circular progress indicator.

import React from 'react';
import { useThemePalette } from '../../hooks/useThemePalette';

interface ProgressRingProps {
  percent: number;     // 0–100
  size?: number;       // outer diameter in px, default 48
  stroke?: number;     // stroke width, default 4
  color?: string;      // arc color, default '#4f8cf7'
  bgColor?: string;    // track color
  label?: string;      // optional centre text (overrides percent display)
  showLabel?: boolean; // show percent text in centre, default true
  fontSize?: number;   // centre label font size, default size/4
  textColor?: string;  // centre text color (overrides theme default)
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  percent,
  size = 48,
  stroke = 4,
  color = '#4f8cf7',
  bgColor,
  label,
  showLabel = true,
  fontSize,
  textColor,
}) => {
  const C = useThemePalette();

  const clamped    = Math.min(100, Math.max(0, percent));
  const radius     = (size - stroke) / 2;
  const circ       = 2 * Math.PI * radius;
  const offset     = circ - (clamped / 100) * circ;
  const cx         = size / 2;
  const cy         = size / 2;
  const labelFs    = fontSize ?? Math.round(size / 4);
  const centreText = label ?? `${Math.round(clamped)}%`;

  const trackColor  = bgColor ?? C.border;
  const labelColor  = textColor ?? (clamped === 100 ? color : C.text);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circ} ${circ}`}
        strokeDashoffset={offset}
        style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.4s ease' }}
      />
      {showLabel && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill={labelColor}
          fontSize={labelFs}
          fontWeight="700"
          fontFamily="inherit"
        >
          {centreText}
        </text>
      )}
    </svg>
  );
};

export default ProgressRing;
